import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { BiArrowBack, BiSend, BiCheckCircle, BiPrinter, BiXCircle, BiEditAlt } from "react-icons/bi";
import { toast } from "react-toastify";

import {
  getInvoiceDetail,
  updateInvoiceStatus,
  clearDetail,
  invoiceV2Selector,
} from "./invoiceV2Slice";
import { Colors } from "../../../assets/Colors";

const STATUS_CONFIG = {
  draft:     { label: "Draft",      bg: "bg-gray-100",  text: "text-gray-600" },
  sent:      { label: "Terkirim",   bg: "bg-blue-100",  text: "text-blue-700" },
  paid:      { label: "Lunas",      bg: "bg-green-100", text: "text-green-700" },
  cancelled: { label: "Dibatalkan", bg: "bg-red-100",   text: "text-red-700" },
};

function fmt(n) {
  if (!n && n !== 0) return "0";
  return new Intl.NumberFormat("id-ID").format(n);
}

// Qty mengikuti opsi invoice: Desimal = 4 angka di belakang koma, Pembulatan = bulat.
function fmtQty(n, mode) {
  const d = mode === "round" ? 0 : 4;
  return new Intl.NumberFormat("id-ID", { minimumFractionDigits: d, maximumFractionDigits: d }).format(Number(n) || 0);
}

function InfoField({ label, value }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-0.5">{label}</p>
      <p className="text-sm text-gray-800">{value || <span className="text-gray-400">—</span>}</p>
    </div>
  );
}

export default function InvoiceDetailV2() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const invNo = location.state?.InvNo ?? null;

  const invV2 = useSelector(invoiceV2Selector);
  const d = invV2.detail;
  const [loading, setLoading] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  useEffect(() => {
    if (invNo) dispatch(getInvoiceDetail(invNo));
    return () => { dispatch(clearDetail()); };
  }, [invNo]); // eslint-disable-line

  if (!invNo) {
    return <div className="md:ml-64 flex items-center justify-center h-64 text-gray-400">
      <button onClick={() => navigate("/invoice-list-v2")} className="text-orange-500 underline">Kembali ke daftar</button>
    </div>;
  }
  if (invV2.isDetailLoading || !d) {
    return <div className="md:ml-64 flex items-center justify-center h-64 text-gray-400">Memuat data…</div>;
  }

  const cfg = STATUS_CONFIG[d.Status] ?? STATUS_CONFIG.draft;

  const changeStatus = async (status, reason = "") => {
    setLoading(true);
    try {
      await dispatch(updateInvoiceStatus({ InvNo: d.InvNo, Status: status, Reason: reason })).unwrap();
      toast.success(status === "cancelled" ? "Invoice dibatalkan" : "Status invoice diupdate");
      setShowCancel(false);
      dispatch(getInvoiceDetail(invNo));
    } catch (e) { toast.error(e?.message ?? (typeof e === "string" ? e : "Gagal update status")); }
    finally { setLoading(false); }
  };

  // Batal hanya untuk Draft & Terkirim; Lunas dan Dibatalkan bersifat final.
  const canCancel = d.Status === "draft" || d.Status === "sent";
  const handleCancel = () => {
    if (!cancelReason.trim()) { toast.error("Alasan pembatalan wajib diisi"); return; }
    changeStatus("cancelled", cancelReason.trim());
  };

  return (
    <div className="md:ml-64 px-4 md:px-6 pb-20">
      <div className="flex items-center justify-between py-4 mb-2">
        <button onClick={() => navigate("/invoice-list-v2")}
          className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900">
          <BiArrowBack className="text-lg" /> Kembali
        </button>
        <div className="flex items-center gap-2">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${cfg.bg} ${cfg.text}`}>{cfg.label}</span>
          <button onClick={() => navigate("/invoice-cetak-v2", { state: { invoice: d } })}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg border text-sm font-medium"
            style={{ borderColor: Colors.primary, color: Colors.primary }}>
            <BiPrinter /> Cetak Invoice
          </button>
          {d.Status === "draft" && (
            <button onClick={() => changeStatus("sent")} disabled={loading}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-white text-sm font-semibold"
              style={{ background: Colors.primary }}>
              <BiSend /> Kirim ke Client
            </button>
          )}
          {d.Status === "sent" && (
            <button onClick={() => changeStatus("paid")} disabled={loading}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700">
              <BiCheckCircle /> Tandai Lunas
            </button>
          )}
          {(d.Status === "draft" || d.Status === "sent") && (
            <button onClick={() => navigate("/invoice-generate-v2", { state: { KlienID: d.KlienID, ReviseInvNo: d.InvNo } })}
              disabled={loading}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg border text-sm font-medium text-amber-700 border-amber-300 hover:bg-amber-50">
              <BiEditAlt /> Revisi
            </button>
          )}
          {canCancel && (
            <button onClick={() => { setCancelReason(""); setShowCancel(true); }} disabled={loading}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg border text-sm font-medium text-red-600 border-red-300 hover:bg-red-50">
              <BiXCircle /> Batalkan
            </button>
          )}
        </div>
      </div>

      {d.Status === "cancelled" && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800">
          <strong>Invoice dibatalkan</strong>
          {d.CancelledAt && <> pada {d.CancelledAt}</>}
          {d.CancelledBy && <> oleh {d.CancelledBy}</>}
          {d.CancelReason && <>. Alasan: <em>{d.CancelReason}</em></>}
          . Item JR-nya sudah dilepas sehingga bisa ditagih ulang atau JR-nya di-recall.
        </div>
      )}

      {showCancel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={() => !loading && setShowCancel(false)}>
          <div className="w-full max-w-md bg-white rounded-xl shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-bold text-gray-800">Batalkan Invoice {d.InvNo}</h3>
            </div>
            <div className="px-6 py-5 space-y-3 text-sm">
              <p className="text-gray-600">
                Pembatalan bersifat <strong>final</strong>. Item JR yang ditagih invoice ini akan dilepas
                sehingga bisa ditagih ulang di invoice baru, atau JR-nya di-recall. Nomor invoice ini tidak dipakai lagi.
              </p>
              <div>
                <label className="block text-xs font-bold uppercase text-gray-600 mb-1">
                  Alasan Pembatalan <span className="text-red-500">*</span>
                </label>
                <textarea rows={3} value={cancelReason} onChange={e => setCancelReason(e.target.value)} autoFocus
                  className="w-full border rounded px-3 py-2 text-sm outline-none"
                  placeholder="Mis. salah CQ, nilai JR perlu direvisi" />
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
              <button onClick={() => setShowCancel(false)} disabled={loading}
                className="px-4 py-2 rounded-lg border text-sm font-medium text-gray-600 hover:bg-gray-100">Kembali</button>
              <button onClick={handleCancel} disabled={loading}
                className="px-4 py-2 rounded-lg text-white text-sm font-semibold bg-red-600 hover:bg-red-700 disabled:opacity-50">
                {loading ? "Memproses…" : "Batalkan Invoice"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-xl border p-5 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          <InfoField label="No. Invoice"  value={d.InvNo} />
          <InfoField label="No. QO"       value={d.QoNo} />
          <InfoField label="Klien"        value={d.KlienName} />
          <InfoField label="CQ"           value={d.CQName} />
          <InfoField label="No. Kontrak"  value={d.NoKontrak} />
          <InfoField label="Tgl Invoice"  value={d.InvoiceDate} />
          <InfoField label="Jatuh Tempo"  value={d.DueDate} />
          <InfoField label="Periode"      value={d.PeriodStart && `${d.PeriodStart} → ${d.PeriodEnd}`} />
          <InfoField label="Bank"         value={d.BankAccount} />
          <InfoField label="Qty"          value={d.QtyRounding === "round" ? "Pembulatan" : "Desimal"} />
          <InfoField label="Revisi Isi"   value={d.RevisionCount ? `${d.RevisionCount}× direvisi` : "Belum pernah"} />
          {d.Description && <InfoField label="Keterangan" value={d.Description} />}
        </div>
      </div>

      {/* Lines */}
      <div className="bg-white rounded-xl border overflow-hidden mb-4">
        <div className="px-5 py-3 border-b font-semibold text-sm" style={{ background: Colors.primary + "15", color: Colors.primary }}>
          Rincian Tagihan ({d.Lines.length} item)
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left">
                <th className="px-3 py-2 text-xs font-semibold text-gray-500">Tgl Kerja</th>
                <th className="px-3 py-2 text-xs font-semibold text-gray-500">No. JR</th>
                <th className="px-3 py-2 text-xs font-semibold text-gray-500">Layanan</th>
                <th className="px-3 py-2 text-xs font-semibold text-gray-500 text-right">Qty</th>
                <th className="px-3 py-2 text-xs font-semibold text-gray-500 text-right">Rate</th>
                <th className="px-3 py-2 text-xs font-semibold text-gray-500 text-right">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {d.Lines.map(l => (
                <tr key={l.InvLineID} className="hover:bg-gray-50">
                  <td className="px-3 py-2.5 text-gray-600">{l.WorkDate}</td>
                  <td className="px-3 py-2.5 font-mono text-xs text-gray-500">{l.JrID}</td>
                  <td className="px-3 py-2.5 text-gray-800">{l.ItemDisplay || l.ItemName}</td>
                  <td className="px-3 py-2.5 text-right text-gray-700 whitespace-nowrap">
                    {fmtQty(l.BilledQty, d.QtyRounding)} {l.SatuanNama || l.SatuanID}
                    {d.QtyRounding === "round" && l.QtyInput !== l.BilledQty && (
                      <span className="block text-[10px] text-gray-400">input {fmtQty(l.QtyInput)}</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right text-gray-600">{fmt(l.Rate)}</td>
                  <td className="px-3 py-2.5 text-right font-medium text-gray-800">{fmt(l.Amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div className="px-5 py-4 border-t bg-gray-50">
          <div className="ml-auto max-w-xs space-y-1">
            <div className="flex justify-between text-sm text-gray-600">
              <span>Subtotal</span><span>Rp {fmt(d.Subtotal)}</span>
            </div>
            <div className="flex justify-between text-sm text-gray-600">
              <span>PPN {d.TaxPct}%</span><span>Rp {fmt(d.TaxAmount)}</span>
            </div>
            <div className="flex justify-between text-base font-bold border-t pt-1.5 mt-1" style={{ color: Colors.primary }}>
              <span>Total</span><span>Rp {fmt(d.TotalAmount)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
